import Heading from '@/components/Typography/Heading';
import useInertiaData from '@/hooks/useInertiaData';
import useRoutes from '@/hooks/useRoutes';
import { useThemeColors } from '@/hooks/useThemeColors';
import { ClassData, CourseData, NextTopicData, TopicData } from '@/types';
import { CheckCircle, ChevronLeft, ChevronRight, Play } from 'lucide-react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface CourseContentSidebarProps {
    course: CourseData;
    topic?: TopicData;
    classData: ClassData;
    isCompleted: boolean;
    nextTopic?: NextTopicData | null;
    isPaused?: boolean;
}

const CourseContentSidebar: React.FC<CourseContentSidebarProps> = ({
    course,
    topic,
    classData,
    isCompleted,
    nextTopic,
    isPaused = false,
}) => {
    const { t } = useTranslation();
    const colors = useThemeColors();
    const { route } = useRoutes();
    const { csrf_token } = useInertiaData();
    const sortedTopicClasses = useMemo(
        () =>
            [...(topic?.classes ?? [])].sort(
                (a, b) =>
                    (a.order ?? Number.MAX_SAFE_INTEGER) -
                        (b.order ?? Number.MAX_SAFE_INTEGER) || a.id - b.id
            ),
        [topic?.classes]
    );

    const handleNext = async (nextUrl: string) => {
        if (!isCompleted && csrf_token) {
            try {
                await fetch(
                    route('class-mark-complete', {
                        slug: course.slug,
                        class_id: classData.id,
                    }).toString(),
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/x-www-form-urlencoded',
                        },
                        body: new URLSearchParams({
                            csrfmiddlewaretoken: csrf_token,
                            action: 'complete',
                        }),
                    }
                );
            } catch (_) {
                // navigate regardless
            }
        }
        window.location.href = nextUrl;
    };

    // Get current class index and navigation
    const currentIndex = sortedTopicClasses.findIndex((c) => c.is_current);
    const previousClass =
        currentIndex > 0 ? sortedTopicClasses[currentIndex - 1] : null;
    const nextClass =
        currentIndex < sortedTopicClasses.length - 1
            ? sortedTopicClasses[currentIndex + 1]
            : null;
    const nextTopicHref =
        nextTopic?.type === 'assignment'
            ? `/assignments/${nextTopic.assignment_slug}/`
            : nextTopic?.class_id
              ? route('class-detail', {
                    slug: course.slug,
                    class_id: nextTopic.class_id,
                }).toString()
              : null;

    return (
        <div className="flex h-full w-full flex-col border-l border-gray-200 bg-white text-gray-900 lg:w-80">
            {/* Sidebar Header */}
            <div className="border-b border-gray-200 p-6">
                <div className="mb-4 flex items-center justify-between">
                    <Heading className="text-lg">
                        {t('course.courseContentSidebar')}
                    </Heading>
                    <span className="text-sm text-gray-500">
                        {t('course.overview')}
                    </span>
                </div>
                <div className="text-sm text-gray-600">
                    <div className="font-medium">{course.title}</div>
                </div>
            </div>

            {/* Topic/Module Content */}
            {topic ? (
                <div className="flex-1 overflow-y-auto">
                    <div className="p-4">
                        <div className="mb-4">
                            <Heading as="h3" className="mb-2">
                                {topic.title}
                            </Heading>
                            <div className="mb-4 text-sm text-gray-500">
                                {sortedTopicClasses.length}{' '}
                                {t('course.lessons', {
                                    count: sortedTopicClasses.length,
                                })}
                            </div>
                        </div>

                        {/* Classes List */}
                        <div className="space-y-2">
                            {sortedTopicClasses.map((classItem) => {
                                // Use isCompleted prop for current class (reflects real-time state),
                                // otherwise use is_completed from backend
                                const classIsCompleted = classItem.is_current
                                    ? isCompleted
                                    : classItem.is_completed;
                                const isComingSoon =
                                    classItem.coming_soon || false;

                                const itemContent = (
                                    <div className="flex items-center space-x-3">
                                        <div className="flex-shrink-0">
                                            {isComingSoon ? (
                                                <div
                                                    className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${colors.border} ${colors.iconBg}`}
                                                />
                                            ) : classIsCompleted ? (
                                                <CheckCircle
                                                    size={20}
                                                    className="text-green-500"
                                                />
                                            ) : (
                                                <div className="flex h-5 w-5 items-center justify-center rounded-full border-2 border-gray-300">
                                                    {classItem.is_current ? (
                                                        <Play
                                                            size={10}
                                                            fill="currentColor"
                                                            className="text-theme-primary-text"
                                                        />
                                                    ) : null}
                                                </div>
                                            )}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div
                                                className={`truncate text-sm font-medium ${isComingSoon ? colors.iconColor : classIsCompleted && !classItem.is_current ? 'text-green-800' : ''}`}
                                            >
                                                {classItem.title}
                                            </div>
                                            {isComingSoon ? (
                                                <div
                                                    className={`mt-1 text-xs font-medium ${colors.iconColor}`}
                                                >
                                                    {t('course.comingSoon')}
                                                </div>
                                            ) : classItem.is_current ? (
                                                <div className="text-theme-primary-text mt-1 text-xs">
                                                    {t(
                                                        'course.currentlyWatching'
                                                    )}
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                );

                                return isComingSoon || isPaused ? (
                                    <div
                                        key={classItem.id}
                                        className={`block cursor-default rounded-lg border p-3 opacity-80 ${colors.border} ${colors.accentBg}`}
                                    >
                                        {itemContent}
                                    </div>
                                ) : (
                                    <a
                                        key={classItem.id}
                                        href={route('class-detail', {
                                            slug: course.slug,
                                            class_id: classItem.id,
                                        }).toString()}
                                        className={`block rounded-lg border p-3 transition-all duration-200 ${
                                            classItem.is_current
                                                ? 'border-theme-border bg-theme-light text-theme-text'
                                                : classIsCompleted
                                                  ? 'border-green-200 bg-green-50'
                                                  : 'border-gray-200 hover:bg-gray-50'
                                        }`}
                                    >
                                        {itemContent}
                                    </a>
                                );
                            })}
                        </div>
                    </div>
                </div>
            ) : null}

            {/* Navigation Footer */}
            {!isPaused && (
                <div className="border-t border-gray-200 bg-gray-50 p-4">
                    <div className="flex items-center justify-between">
                        {previousClass ? (
                            <a
                                href={route('class-detail', {
                                    slug: course.slug,
                                    class_id: previousClass.id,
                                }).toString()}
                                className="flex items-center space-x-2 text-gray-600 transition-colors hover:text-gray-900"
                            >
                                <ChevronLeft size={16} />
                                <span className="text-sm font-medium">
                                    {t('course.previous')}
                                </span>
                            </a>
                        ) : (
                            <div></div>
                        )}

                        {nextClass ? (
                            <a
                                href={route('class-detail', {
                                    slug: course.slug,
                                    class_id: nextClass.id,
                                }).toString()}
                                onClick={(e) => {
                                    e.preventDefault();
                                    void handleNext(
                                        route('class-detail', {
                                            slug: course.slug,
                                            class_id: nextClass.id,
                                        }).toString()
                                    );
                                }}
                                className="btn-gradient flex items-center space-x-2 rounded-lg px-4 py-2 text-sm font-medium"
                            >
                                <span>{t('course.next')}</span>
                                <ChevronRight size={16} />
                            </a>
                        ) : null}
                        {!nextClass && nextTopic && nextTopicHref ? (
                            <a
                                href={nextTopicHref}
                                onClick={(e) => {
                                    e.preventDefault();
                                    void handleNext(nextTopicHref);
                                }}
                                className="btn-gradient flex items-center space-x-2 rounded-lg px-4 py-2 text-sm font-medium"
                            >
                                <span>
                                    {nextTopic.type === 'assignment'
                                        ? t('course.nextAssignment')
                                        : t('course.nextTopic')}
                                </span>
                                <ChevronRight size={16} />
                            </a>
                        ) : null}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CourseContentSidebar;
